import { createGlobalState, GlobalState } from '../../globalState';
import { BucketBase, type BucketMigration } from './BucketBase';

/* VERSIONING GUIDE:
 1) Add update in the specific state (globalState, layerState, DesignSchemeState)
 2) Bump latestVersion when the persisted schema changes.
 3) Add migrateLayersBucketV# function where the change is made
 4) Register it in LAYERS_BUCKET_MIGRATIONS with key N.
EXAMPLE:
 - Add createGlobalState function (gobalState file) enableAutoSave : boolean in  globalState.settings
 - Bump latestVersion in GlobalBucket to 2
 - Add migrateLayersBucketV2 function like this:
    const migrateGlobalBucketV2 = (state: GlobalBucketState): GlobalBucketState => ({
        ...state,
        settings: {
            ...createGlobalState().settings,
            ...state.settings,
            enableAutoSave: state.settings?.enableAutoSave ?? createGlobalState().settings.enableAutoSave,
        },
    }); 
 - Add in LAYERS_BUCKET_MIGRATIONS: 2: migrateGlobalBucketV2
 */

export type GlobalBucketState = {
    activeGridId: string;
} & GlobalState;

export class GlobalBucket extends BucketBase<GlobalBucketState> {
    readonly id = 'global';
    readonly latestVersion = 6;

    protected getMigrations(): Record<number, BucketMigration<GlobalBucketState>> {
        return GLOBAL_BUCKET_MIGRATIONS;
    }

    getDefault(): GlobalBucketState {
        return {
            ...createGlobalState(),
            activeGridId: '',
        };
    }

    migrate(persisted: unknown, version = 0): GlobalBucketState {
        const persistedState = (persisted as GlobalBucketState | undefined) ?? undefined;
        if (!persistedState) {
            return this.getDefault();
        }

        const baseState = migrateGlobalBucketV2(persistedState);
        const normalizedState = migrateGlobalBucketV6(baseState);

        if (version >= this.latestVersion) {
            return normalizedState;
        }

        return this.applyMigrations(baseState, Math.max(version, 2));
    }
}

const migrateGlobalBucketV1 = (state: GlobalBucketState): GlobalBucketState => state;

const migrateGlobalBucketV2 = (state: GlobalBucketState): GlobalBucketState => {
    const { performanceMetrics: _performanceMetrics, ...rest } = (state as GlobalBucketState & { performanceMetrics?: unknown }) ?? state;
    return rest as GlobalBucketState;
};

const migrateGlobalBucketV3 = (state: GlobalBucketState): GlobalBucketState => ({
    ...migrateGlobalBucketV2(state),
    settings: {
        ...createGlobalState().settings,
        ...state.settings,
        coordinateSystem: state.settings?.coordinateSystem ?? createGlobalState().settings.coordinateSystem,
    },
});

const migrateGlobalBucketV4 = (state: GlobalBucketState): GlobalBucketState => ({
    ...migrateGlobalBucketV3(state),
    ambient: {
        ...createGlobalState().ambient,
        ...state.ambient,
        showStrokes: state.ambient?.showStrokes ?? createGlobalState().ambient.showStrokes,
    },
});

const migrateGlobalBucketV5 = (state: GlobalBucketState): GlobalBucketState => ({
    ...migrateGlobalBucketV4(state),
    settings: {
        ...createGlobalState().settings,
        ...state.settings,
        coloredItems:
            state.settings?.coloredItems ??
            (state.settings?.colorScheme === 'Light Grey blue & green' || state.settings?.colorScheme === 'White blue & green'),
    },
});

const migrateGlobalBucketV6 = (state: GlobalBucketState): GlobalBucketState => ({
    ...migrateGlobalBucketV5(state),
    data: {
        ...createGlobalState().data,
        ...state.data,
        showSelected: state.data?.showSelected ?? createGlobalState().data.showSelected,
    },
});

const GLOBAL_BUCKET_MIGRATIONS: Record<number, BucketMigration<GlobalBucketState>> = {
    1: migrateGlobalBucketV1,
    2: migrateGlobalBucketV2,
    3: migrateGlobalBucketV3,
    4: migrateGlobalBucketV4,
    5: migrateGlobalBucketV5,
    6: migrateGlobalBucketV6,
};
